import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Pin, GitCompare, X, Loader2, ArrowUpDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PinnedPairsRow — Round 89e (Feb 2026).
 *
 * Renders the user's pinned Compare-modal pairs as a horizontally-scrollable
 * row of mini face-off cards on the Watchlist tab. Mirrors the visual
 * language of WatchlistTimeMachineBar (mini-card + inline metric) so the
 * tab keeps a consistent rhythm.
 *
 * Each card shows: A vs B, both current yields fetched in batch from
 * `/api/stocks/batch/...`, an X to unpin, and the whole card opens the
 * CompareModal pre-locked to that pair via the `onOpenPair(a, b)` prop.
 *
 * Refreshes whenever:
 *   1. The component mounts (`open` true)
 *   2. The user toggles a pin from CompareModal (listens for
 *      window `compare-pins-changed` event)
 *
 * Hidden entirely when there are no pins — zero footprint for new users.
 */
export default function PinnedPairsRow({ user, onOpenPair }) {
  const [pins, setPins] = useState([]);
  const [yields, setYields] = useState({}); // ticker → { yield, price }
  const [loading, setLoading] = useState(true);
  const [unpinningKey, setUnpinningKey] = useState(null);
  // Round 89g — sort key persists per user across reloads so an
  // intentional preference (e.g. "show me the biggest yield gaps")
  // sticks. Default mirrors server behavior (newest pin first).
  const [sortKey, setSortKey] = useState(() => {
    try { return localStorage.getItem('dividrip:pinned-pairs-sort') || 'recent'; }
    catch { return 'recent'; }
  });
  useEffect(() => {
    try { localStorage.setItem('dividrip:pinned-pairs-sort', sortKey); }
    catch { /* swallow */ }
  }, [sortKey]);

  const fetchPins = useCallback(async () => {
    if (!user) {
      setPins([]);
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/compare-pins`, { withCredentials: true });
      setPins(Array.isArray(data) ? data : []);
    } catch {
      setPins([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchPins();
    const handler = () => fetchPins();
    window.addEventListener('compare-pins-changed', handler);
    return () => window.removeEventListener('compare-pins-changed', handler);
  }, [fetchPins]);

  // Batch-fetch yields whenever the pin list changes. Skip already-loaded
  // tickers so the same yields aren't re-fetched on every pin/unpin.
  useEffect(() => {
    if (pins.length === 0) return;
    const tickers = Array.from(new Set(pins.flatMap((p) => [p.a, p.b]).filter(Boolean)));
    const missing = tickers.filter((t) => !(t in yields));
    if (missing.length === 0) return;
    axios
      .get(`${API}/stocks/batch/${missing.join(',')}`, { withCredentials: true })
      .then(({ data }) => {
        const next = { ...yields };
        (data?.stocks || data || []).forEach((s) => {
          if (s?.ticker) {
            next[s.ticker.toUpperCase()] = {
              yield: s.dividend_yield ?? null,
              price: s.price ?? null,
            };
          }
        });
        setYields(next);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pins]);

  const handleUnpin = useCallback(
    async (a, b) => {
      const key = `${a}__${b}`;
      if (unpinningKey === key) return;
      setUnpinningKey(key);
      // Optimistic remove
      setPins((prev) => prev.filter((p) => !(p.a === a && p.b === b)));
      try {
        await axios.delete(`${API}/compare-pins`, {
          params: { a, b },
          withCredentials: true,
        });
        toast.success(`Unpinned ${a} vs ${b}`);
        window.dispatchEvent(new Event('compare-pins-changed'));
      } catch {
        toast.error('Could not unpin — refresh to retry');
        fetchPins();
      } finally {
        setUnpinningKey(null);
      }
    },
    [unpinningKey, fetchPins],
  );

  // Round 89g — Sorted view of the pins for the visible row. Yield-gap
  // sort surfaces dwarf face-offs to the top (e.g. NATH 2.81% vs PEB
  // 0.31% = 2.50pp gap); alphabetical groups same-letter pairs;
  // recent matches the server's natural pinned_at desc order.
  // Computed before any early return so the Hooks-call order stays
  // stable across renders (react-hooks/rules-of-hooks).
  const sortedPins = useMemo(() => {
    const arr = [...pins];
    if (sortKey === 'gap') {
      arr.sort((p, q) => {
        const pg = Math.abs((yields[p.a]?.yield ?? 0) - (yields[p.b]?.yield ?? 0));
        const qg = Math.abs((yields[q.a]?.yield ?? 0) - (yields[q.b]?.yield ?? 0));
        return qg - pg; // biggest gap first
      });
    } else if (sortKey === 'alpha') {
      arr.sort((p, q) => `${p.a} ${p.b}`.localeCompare(`${q.a} ${q.b}`));
    }
    // 'recent' is the default; server already returns pinned_at desc.
    return arr;
  }, [pins, sortKey, yields]);

  if (loading || !user || pins.length === 0) return null;

  return (
    <div className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/5 dark:bg-amber-500/10 p-3" data-testid="pinned-pairs-row">
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <h3 className="text-[11px] font-bold uppercase tracking-[0.15em] text-amber-700 dark:text-amber-300 flex items-center gap-1.5">
          <Pin className="w-3.5 h-3.5" />
          Pinned Comparisons
          <span className="text-muted-foreground/70 font-mono font-normal tracking-normal">
            {pins.length}
          </span>
        </h3>
        <div className="flex items-center gap-2 ml-auto">
          {/* Round 89g — Sort dropdown. Surfaces only when there are
              at least 2 pins (single-pin row has no order to sort). */}
          {pins.length >= 2 && (
            <label className="inline-flex items-center gap-1 text-[10px] text-muted-foreground" data-testid="pinned-pairs-sort-label">
              <ArrowUpDown className="w-3 h-3" />
              <span className="hidden sm:inline">Sort:</span>
              <select
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value)}
                className="bg-transparent border border-border rounded px-1 py-0.5 text-[10px] font-semibold focus:outline-none focus:ring-1 focus:ring-amber-500/40 cursor-pointer"
                data-testid="pinned-pairs-sort-select"
                title="Re-order your pinned face-off cards"
              >
                <option value="recent">Most recently pinned</option>
                <option value="gap">Largest yield gap</option>
                <option value="alpha">Alphabetical</option>
              </select>
            </label>
          )}
          <span className="text-[10px] text-muted-foreground italic hidden md:inline">
            One-click face-off · saved across devices
          </span>
        </div>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 snap-x snap-mandatory">
        {sortedPins.map((p) => {
          const a = p.a;
          const b = p.b;
          const key = `${a}__${b}`;
          const ayd = yields[a]?.yield;
          const byd = yields[b]?.yield;
          const ap = yields[a]?.price;
          const bp = yields[b]?.price;
          return (
            <div
              key={key}
              className="group relative shrink-0 snap-start w-[180px] sm:w-[200px] rounded-md border bg-card hover:bg-muted/40 transition-colors p-2 cursor-pointer"
              onClick={() => onOpenPair?.(a, b)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onOpenPair?.(a, b);
                }
              }}
              data-testid={`pinned-pair-card-${a}-${b}`}
              title={`Open Compare: ${a} vs ${b}`}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleUnpin(a, b);
                }}
                disabled={unpinningKey === key}
                className="absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center text-muted-foreground hover:text-rose-500 hover:bg-rose-500/10 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
                aria-label={`Unpin ${a} vs ${b}`}
                data-testid={`pinned-pair-unpin-${a}-${b}`}
                title="Unpin"
              >
                {unpinningKey === key ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
              </button>
              <div className="flex items-center gap-1.5 mb-1.5">
                <GitCompare className="w-3 h-3 text-muted-foreground shrink-0" />
                <span className="font-mono text-xs font-bold text-sky-600 dark:text-sky-400 truncate">{a}</span>
                <span className="text-[10px] text-muted-foreground">vs</span>
                <span className="font-mono text-xs font-bold text-violet-600 dark:text-violet-400 truncate">{b}</span>
              </div>
              <div className="grid grid-cols-2 gap-1 text-[10px] font-mono">
                <div className="flex flex-col">
                  <span className="text-muted-foreground">Yield</span>
                  <span className="font-bold text-sky-700 dark:text-sky-300">
                    {ayd != null ? `${Number(ayd).toFixed(2)}%` : '—'}
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="text-muted-foreground">Yield</span>
                  <span className="font-bold text-violet-700 dark:text-violet-300">
                    {byd != null ? `${Number(byd).toFixed(2)}%` : '—'}
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="text-muted-foreground">Price</span>
                  <span className="text-sky-700/80 dark:text-sky-300/80">
                    {ap != null ? `$${Number(ap).toFixed(2)}` : '—'}
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="text-muted-foreground">Price</span>
                  <span className="text-violet-700/80 dark:text-violet-300/80">
                    {bp != null ? `$${Number(bp).toFixed(2)}` : '—'}
                  </span>
                </div>
              </div>
              {/* Round 89f — Memo line. Only renders when the pin
                  carries a non-empty note. Italic + amber + truncated;
                  full text in the title attr for hover-tooltip. */}
              {p.note && (
                <div
                  className="mt-1.5 pt-1.5 border-t border-amber-500/20 text-[10px] italic text-amber-700 dark:text-amber-300 truncate"
                  title={p.note}
                  data-testid={`pinned-pair-note-${a}-${b}`}
                >
                  &ldquo;{p.note}&rdquo;
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
